import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Avatar,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AdminTable from '../components/AdminTable';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { getImageUrl, handleImageError } from '../utils/imageUtils';
import { getAllContent, createContent, updateContent, deleteContent, getAllCategories } from '../api/apiService';

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [filterCategory, setFilterCategory] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [categories, setCategories] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchCategories = async () => {
    try {
      const data = await getAllCategories('News');
      setCategories(data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchNews = async () => {
    setLoading(true);
    try {
      const sortStr = order === 'desc' ? `-${orderBy}` : orderBy;
      const data = await getAllContent('News', page + 1, rowsPerPage, searchQuery, sortStr, filterCategory, dateFilter);
      setNews(data.data || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    fetchCategories();
  }, [page, rowsPerPage, searchQuery, orderBy, order, filterCategory, dateFilter]);

  const handleFilter = (type, value) => {
    if (type === 'category') setFilterCategory(value);
    if (type === 'date') setDateFilter(value);
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const columns = [
    {
      id: 'image',
      label: 'Image',
      render: (value) => (
        <Avatar 
          src={getImageUrl(value)} 
          variant="rounded" 
          sx={{ width: 40, height: 40 }} 
          slotProps={{ img: { onError: handleImageError } }}
        />
      ),
    },
    { id: 'title', label: 'Title' },
    { id: 'category', label: 'Category' },
    { id: 'author', label: 'Author' },
    { id: 'date', label: 'Date', render: (val) => new Date(val).toLocaleDateString() },
    {
      id: 'status',
      label: 'Status',
      render: (value) => (
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            display: 'inline-block',
            bgcolor: value === 'Published' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(102, 102, 102, 0.1)',
            color: value === 'Published' ? '#2E7D32' : '#666',
          }}
        >
          {value}
        </Box>
      ),
    },
  ];

  const handleOpen = (item = null) => {
    if (item) {
      setEditingNews({ 
        ...item, 
        content: item.description || '',
        description: item.shortDescription || '',
        status: item.status === 'Active' ? 'Published' : item.status
      });
      setImagePreview(item.image);
    } else {
      setEditingNews({ title: '', category: '', author: '', date: new Date().toISOString().split('T')[0], status: 'Draft', content: '', description: '' });
      setImagePreview('');
    }
    setImageFile(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingNews(null);
    setImageFile(null);
    setImagePreview('');
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append('title', editingNews.title);
    formData.append('category', editingNews.category);
    formData.append('author', editingNews.author);
    formData.append('date', editingNews.date);
    formData.append('status', editingNews.status === 'Published' ? 'Active' : editingNews.status);
    formData.append('description', editingNews.content);
    formData.append('shortDescription', editingNews.description || '');
    formData.append('type', 'News');

    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      if (editingNews.id) {
        await updateContent(editingNews.id, formData);
      } else {
        await createContent(formData);
      }
      fetchNews();
      handleClose();
    } catch (error) {
      console.error('Error saving news:', error);
      alert(error.response?.data?.message || 'Error saving news');
    }
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteContent(itemToDelete.id);
        fetchNews();
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      } catch (error) {
        console.error('Error deleting news:', error);
      }
    }
  };

  const filteredNews = news; // Now filtered on server

  return (
    <Box>
      <AdminTable
        title="News Management"
        columns={columns}
        data={filteredNews}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        loading={loading}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        onSearch={setSearchQuery}
        onSort={handleSort}
        onFilter={handleFilter}
        categories={categories}
        onAdd={() => handleOpen()}
        onEdit={(item) => handleOpen(item)}
        onDelete={handleDelete}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: '16px', p: 1 },
          }
        }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {editingNews?.id ? 'Edit News' : 'Add New News'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderBottom: 'none' }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="News Title"
                variant="outlined"
                value={editingNews?.title || ''}
                onChange={(e) => setEditingNews({ ...editingNews, title: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Category"
                select
                variant="outlined"
                value={editingNews?.category || ''}
                onChange={(e) => setEditingNews({ ...editingNews, category: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id || cat._id} value={cat.name}>{cat.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Author"
                variant="outlined"
                value={editingNews?.author || ''}
                onChange={(e) => setEditingNews({ ...editingNews, author: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Publication Date"
                type="date"
                variant="outlined"
                value={editingNews?.date ? new Date(editingNews.date).toISOString().split('T')[0] : ''}
                onChange={(e) => setEditingNews({ ...editingNews, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Status"
                select
                variant="outlined"
                value={editingNews?.status || 'Draft'}
                onChange={(e) => setEditingNews({ ...editingNews, status: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Published">Published</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Short Description"
                variant="outlined"
                multiline
                rows={2}
                value={editingNews?.description || ''}
                onChange={(e) => setEditingNews({ ...editingNews, description: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <input
                type="file"
                accept="image/*"
                id="news-image-upload"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
              <label htmlFor="news-image-upload">
                <Box
                  sx={{
                    border: '2px dashed #E0E0E0',
                    borderRadius: '12px',
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': { borderColor: '#F25828', bgcolor: 'rgba(242, 88, 40, 0.02)' },
                  }}
                >
                  {imagePreview ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={imagePreview}
                        sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: '8px' }}
                      />
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.8)' }}
                        onClick={(e) => {
                          e.preventDefault();
                          setImageFile(null);
                          setImagePreview('');
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <UploadIcon sx={{ fontSize: 40, color: '#999', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Click to upload cover image or drag and drop
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        PNG, JPG or WebP (max. 2MB)
                      </Typography>
                    </>
                  )}
                </Box>
              </label>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#666' }}>
                Content
              </Typography>
              <Box sx={{ 
                '& .quill': { borderRadius: '10px', overflow: 'hidden' },
                '& .ql-toolbar': { borderTopLeftRadius: '10px', borderTopRightRadius: '10px', borderColor: '#E0E0E0' },
                // ... (styles removed for brevity in thinking but preserved in actual writing)
                '& .ql-container': { borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px', borderColor: '#E0E0E0', minHeight: '200px' }
              }}>
                <ReactQuill
                  theme="snow"
                  value={editingNews?.content || ''}
                  onChange={(content) => setEditingNews({ ...editingNews, content })}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      ['link', 'clean']
                    ],
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            sx={{ color: '#666', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#F25828',
              '&:hover': { bgcolor: '#d44a20' },
              borderRadius: '10px',
              textTransform: 'none',
              px: 4,
              fontWeight: 600,
            }}
          >
            {editingNews?.id ? 'Update News' : 'Save News'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete News Item?"
        message={`Are you sure you want to delete "${itemToDelete?.title}"? This action cannot be undone.`}
      />
    </Box>
  );
};

export default AdminNews;
